import { isValid, parse } from 'date-fns';
import { dateOptions } from 'utils/constants';

type FilterType = 'free' | 'select' | 'date' | 'none';
type ColumnType = 'String' | 'Date' | 'Number' | 'LocalizableString' | 'LocalizableStringFe';
type ColumnKind = 'date' | 'number' | 'string';
interface ColumnConfig {
  idTemplateColumn: number;
  name: string;
  position: number;
  width: number;
  translate: string;
  type: ColumnType;
  filterType: FilterType;
}

interface TableConfig {
  columnsList: ColumnConfig[];
  rows: Record<string, string | number | null>[];
}

function formatTranslate(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function toCamelCase(raw: string): string {
  return raw.toLowerCase().replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

// eslint-disable-next-line
const FORMATS = [...dateOptions, "yyyy-MM-dd'T'HH:mm:ssX"];

function isDateString(value: string): boolean {
  return FORMATS.some((fmt) => {
    const parsed = parse(value, fmt, new Date());
    return isValid(parsed);
  });
}

function allSamplesAreDate(samples: string[]): boolean {
  const nonEmpty = samples.filter(Boolean);
  return nonEmpty.length > 0 && nonEmpty.every((v) => isDateString(v.trim()));
}
function normalizeToTimestamp(value: string): number | null {
  if (!value) return null;
  const v = value.trim();

  const isoMatch = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }

  const parts = v.split(/[/.-]/);
  if (parts.length !== 3) return null;

  let day: string;
  let month: string;
  let year: string;
  if (parts[0].length === 4) {
    [year, month, day] = parts;
  } else {
    [day, month, year] = parts;
  }

  return Date.UTC(Number(year), Number(month) - 1, Number(day));
}

function inferKind(name: string, samples: string[]): ColumnKind {
  const lc = name.toLowerCase();

  if (
    lc.includes('amount') ||
    lc.includes('amt') ||
    lc.includes('rent') ||
    lc.includes('overdue') ||
    lc.includes('ic_') ||
    lc.includes('days')
  ) {
    return 'number';
  }

  const nonEmpty = samples.filter(Boolean);
  if (nonEmpty.length === 0) return 'string';

  if (allSamplesAreDate(samples)) return 'date';

  const allNumeric = nonEmpty.every((v) => !isNaN(Number(v.replace(',', '.'))));
  if (allNumeric) return 'number';

  return 'string';
}

function kindToType(kind: ColumnKind): ColumnType {
  if (kind === 'date') return 'Date';
  if (kind === 'number') return 'Number';
  return 'String';
}

function kindToFilterType(kind: ColumnKind): FilterType {
  if (kind === 'date') return 'date';
  return 'free';
}

/**
 * Infers the column width based on type and header length.
 * @param {ColumnType} type - The column type
 * @param {number} headerLength - The length of the header text
 * @return {number} The inferred width
 */
function inferWidth(type: ColumnType, headerLength: number): number {
  const base: Record<ColumnType, number> = {
    Date: 160,
    Number: 120,
    String: 180,
    LocalizableString: 200,
    LocalizableStringFe: 160,
  };
  return Math.max(base[type], headerLength * 9);
}

/**
 * Parses a CSV string and returns { columnsList, rows } matching the
 * expected table-config shape.
 *
 * @param {string} csvText  Raw CSV content (first row = headers)
 * @return {TableConfig} The parsed table configuration

 */
export function parseCsvToTableConfig(csvText: string): TableConfig {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length === 0 || lines[0].trim() === '') return { columnsList: [], rows: [] };

  // ── 1. Parse raw rows ──────────────────────────────────────────────────────
  // Auto-detect delimiter: prefer ';' if it appears more often than ',' in the header
  const firstLine = lines[0];
  const delimiter = firstLine.split(';').length - 1 >= firstLine.split(',').length - 1 ? ';' : ',';

  const parseRow = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let insideQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (insideQuote && line[i + 1] === '"') {
          current += '"';
          i++;
        } else insideQuote = !insideQuote;
      } else if (ch === delimiter && !insideQuote) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const rawHeaders = parseRow(lines[0]);
  const rawDataRows = lines.slice(1).map(parseRow);

  // ── 2. Build columnsList ───────────────────────────────────────────────────
  const columnsList: ColumnConfig[] = rawHeaders.map((header, index) => {
    const name = toCamelCase(header);
    const translate = formatTranslate(header);

    const samples = rawDataRows.map((r) => r[index] ?? '').filter(Boolean);

    const kind = inferKind(header, samples);
    const type = kindToType(kind);
    const filterType = kindToFilterType(kind);
    const width = inferWidth(type, name.length);

    return {
      idTemplateColumn: 1000 + index,
      name,
      position: index,
      width,
      translate,
      type,
      filterType,
    };
  });

  // ── 3. Build rows ──────────────────────────────────────────────────────────
  const kindMap = new Map<string, ColumnKind>(
    rawHeaders.map((header, i) => {
      const samples = rawDataRows.map((r) => r[i] ?? '').filter(Boolean);
      return [columnsList[i].name, inferKind(header, samples)];
    }),
  );

  const rows: Record<string, string | number | null>[] = rawDataRows.map((rawRow) => {
    const obj: Record<string, string | number | null> = {};
    columnsList.forEach((col, i) => {
      const raw = rawRow[i] ?? null;
      if (raw === '' || raw === null) {
        obj[col.name] = null;
        return;
      }
      const kind = kindMap.get(col.name);
      if (kind === 'date') {
        obj[col.name] = normalizeToTimestamp(raw);
      } else if (kind === 'number') {
        const parsed = parseFloat(raw.replace(',', '.'));
        obj[col.name] = isNaN(parsed) ? raw : parsed;
      } else {
        obj[col.name] = raw;
      }
    });
    return obj;
  });

  return { columnsList, rows };
}
